import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { X, Trash2, ImagePlus, Save, AlertTriangle, RotateCw, RotateCcw, Stamp } from "lucide-react";
import { PRODUCT_SHEET_FIELDS, REQUIRED_KEYS, EMPTY_SHEET } from "./product-sheet-fields";

function PhotoTile({ photo, onDelete, onRotated }) {
  const [src, setSrc] = useState(null);
  const [cacheKey, setCacheKey] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    let url = null;
    api.get(`/admin/photos/${photo.id}`, { responseType: "blob" })
      .then((r) => {
        if (!active) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo.id, cacheKey]);

  const rotate = async (direction) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/admin/codes/${photo.codeId}/photos/${photo.id}/rotate?direction=${direction}`);
      setCacheKey((k) => k + 1);
      onRotated?.(photo.id);
    } catch {
      toast.error("Rotation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="relative rounded-lg overflow-hidden border group"
      style={{ borderColor: "var(--vault-border)", background: "var(--vault-bg)" }}
      data-testid={`photo-tile-${photo.id}`}
    >
      <div className="aspect-square flex items-center justify-center">
        {src ? (
          <img src={src} alt={photo.original_filename} className="w-full h-full object-cover" />
        ) : (
          <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
        )}
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(10,10,12,0.7)" }}>
            <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
          </div>
        )}
      </div>

      {/* Action overlay — visible on hover (and always on touch) */}
      <div className="absolute inset-x-0 bottom-0 p-1.5 flex justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.7))" }}>
        <button
          type="button"
          onClick={() => rotate("ccw")}
          disabled={busy}
          className="p-1.5 rounded-full bg-white/10 hover:bg-white/25 transition-colors"
          data-testid={`photo-rotate-ccw-${photo.id}`}
          title="Rotate left"
        >
          <RotateCcw className="w-3.5 h-3.5 text-white" />
        </button>
        <button
          type="button"
          onClick={() => rotate("cw")}
          disabled={busy}
          className="p-1.5 rounded-full bg-white/10 hover:bg-white/25 transition-colors"
          data-testid={`photo-rotate-cw-${photo.id}`}
          title="Rotate right"
        >
          <RotateCw className="w-3.5 h-3.5 text-white" />
        </button>
      </div>

      <button
        type="button"
        onClick={() => onDelete(photo.id)}
        className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/70 hover:bg-red-600 transition-colors"
        data-testid={`photo-delete-${photo.id}`}
        title="Remove photo"
      >
        <Trash2 className="w-3.5 h-3.5 text-white" />
      </button>
    </div>
  );
}

export default function ProductSheetModal({ codeRecord, onClose, onSaved }) {
  const [values, setValues] = useState(EMPTY_SHEET);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errors, setErrors] = useState({});
  const inputRef = useRef(null);
  const hasSavedSheet = Boolean(codeRecord.product_sheet);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Hydrate from the code record we already have
        if (codeRecord.product_sheet) {
          const merged = { ...EMPTY_SHEET };
          for (const k of Object.keys(EMPTY_SHEET)) {
            merged[k] = codeRecord.product_sheet[k] || "";
          }
          if (!cancelled) setValues(merged);
        }
        const r = await api.get(`/admin/codes/${codeRecord.id}/photos`);
        if (!cancelled) setPhotos(r.data);
      } catch {
        if (!cancelled) toast.error("Failed to load product sheet");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [codeRecord]);

  const onChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    for (const k of REQUIRED_KEYS) {
      if (!(values[k] || "").trim()) next[k] = "Required";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      const trimmed = {};
      for (const k of Object.keys(values)) trimmed[k] = (values[k] || "").trim();
      await api.put(`/admin/codes/${codeRecord.id}/product-sheet`, trimmed);
      toast.success("Product sheet saved");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const generateCert = async () => {
    if (!hasSavedSheet) {
      toast.error("Save the product sheet first");
      return;
    }
    if (!window.confirm(
      "Generate a stamped digital certificate?\n\nThis will create a new PDF, apply an RFC 3161 trusted timestamp via FreeTSA, and link it as the certificate for this code. Any previously auto-generated certificate is replaced."
    )) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/codes/${codeRecord.id}/generate-certificate`);
      toast.success(`Certificate generated · ${data.file_name}`);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const onPhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 10) {
      toast.error("Maximum 10 photos per bottle");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/codes/${codeRecord.id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((p) => [...p, r.data]);
      toast.success("Photo added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/admin/codes/${codeRecord.id}/photos/${photoId}`);
      setPhotos((p) => p.filter((x) => x.id !== photoId));
      toast.success("Photo removed");
    } catch {
      toast.error("Failed to remove photo");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      data-testid="product-sheet-modal"
      onClick={onClose}
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-2xl p-8 max-w-4xl w-full fade-up max-h-[90vh] flex flex-col"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="overline text-[var(--vault-text-muted)]">Bottle Passport</p>
            <h3 className="font-display text-2xl mt-1">Product Sheet · <span className="font-mono-vault text-[var(--vault-gold)]">{codeRecord.code}</span></h3>
            <p className="text-sm text-[var(--vault-text-muted)] mt-1">{codeRecord.label || "Untitled"}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="sheet-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
          </div>
        ) : (
          <div className="flex-1 overflow-auto pr-2 space-y-8">
            {/* Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PRODUCT_SHEET_FIELDS.map((f) => {
                const currentValue = values[f.key] || "";
                const hasUnknownLegacy = f.options && currentValue && !f.options.includes(currentValue);
                return (
                  <div key={f.key} className={f.multiline ? "sm:col-span-2" : ""}>
                    <label className="overline block text-[var(--vault-text-muted)] mb-2 flex items-center gap-1">
                      {f.label} {f.required && <span style={{ color: "var(--vault-gold)" }}>·</span>}
                      {!f.required && <span className="text-[var(--vault-text-faint)] normal-case">(optional)</span>}
                    </label>
                    {f.options ? (
                      <select
                        value={currentValue}
                        onChange={(e) => onChange(f.key, e.target.value)}
                        data-testid={`sheet-${f.key}`}
                        className="w-full px-3 py-2.5 rounded text-sm outline-none transition-colors appearance-none cursor-pointer"
                        style={{
                          background: "var(--vault-bg)",
                          border: `1px solid ${errors[f.key] ? "#FCA5A5" : "var(--vault-border)"}`,
                          backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23C5A059' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "right 12px center",
                          paddingRight: "32px",
                        }}
                      >
                        <option value="">— Select {f.label} —</option>
                        {f.options.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                        {hasUnknownLegacy && (
                          <option value={currentValue}>{currentValue} (legacy)</option>
                        )}
                      </select>
                    ) : f.multiline ? (
                      <textarea
                        value={currentValue}
                        onChange={(e) => onChange(f.key, e.target.value)}
                        data-testid={`sheet-${f.key}`}
                        rows={4}
                        placeholder="Notes about provenance, storage history, tasting comments…"
                        className="w-full px-3 py-2.5 rounded text-sm outline-none transition-colors resize-y"
                        style={{
                          background: "var(--vault-bg)",
                          border: `1px solid ${errors[f.key] ? "#FCA5A5" : "var(--vault-border)"}`,
                          minHeight: "100px",
                        }}
                      />
                    ) : (
                      <input
                        value={currentValue}
                        onChange={(e) => onChange(f.key, e.target.value)}
                        data-testid={`sheet-${f.key}`}
                        className="w-full px-3 py-2.5 rounded text-sm outline-none transition-colors"
                        style={{
                          background: "var(--vault-bg)",
                          border: `1px solid ${errors[f.key] ? "#FCA5A5" : "var(--vault-border)"}`,
                        }}
                      />
                    )}
                    {errors[f.key] && (
                      <p className="text-[10px] mt-1 inline-flex items-center gap-1" style={{ color: "#FCA5A5" }}>
                        <AlertTriangle className="w-3 h-3" /> {errors[f.key]}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Photos */}
            <div>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <p className="overline text-[var(--vault-text-muted)]">Bottle Photos</p>
                  <p className="text-xs text-[var(--vault-text-faint)] mt-1">Up to 10 images (PNG, JPEG, WebP) — max 15MB each</p>
                </div>
                <p className="text-xs text-[var(--vault-text-muted)] font-mono-vault">{photos.length} / 10</p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {photos.map((p) => (
                  <PhotoTile
                    key={p.id}
                    photo={{ ...p, codeId: codeRecord.id }}
                    onDelete={removePhoto}
                  />
                ))}
                {photos.length < 10 && (
                  <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid="photo-add-btn"
                    className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 text-xs text-[var(--vault-text-muted)] hover:border-[var(--vault-gold)] hover:text-white transition-colors"
                    style={{ borderColor: "var(--vault-border)", background: "var(--vault-bg)" }}
                  >
                    {uploading ? (
                      <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
                    ) : (
                      <>
                        <ImagePlus className="w-6 h-6" style={{ color: "var(--vault-gold)" }} />
                        Add photo
                      </>
                    )}
                  </button>
                )}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={onPhotoSelect}
                className="hidden"
                data-testid="photo-file-input"
              />
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-between items-center gap-2 pt-6 mt-6 border-t" style={{ borderColor: "var(--vault-border)" }}>
          <button
            type="button"
            onClick={generateCert}
            disabled={!hasSavedSheet || generating || loading}
            data-testid="sheet-generate-cert-btn"
            title={hasSavedSheet ? "Generate a stamped PDF certificate and attach it to this code" : "Save the sheet first"}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-40"
            style={{
              background: "transparent",
              border: "1px solid var(--vault-gold)",
              color: "var(--vault-gold)",
            }}
          >
            {generating ? (
              <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
            ) : (
              <>
                <Stamp className="w-4 h-4" />
                Generate stamped certificate
              </>
            )}
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
            <button
              type="submit"
              disabled={saving || loading}
              data-testid="sheet-save-btn"
              className="btn-gold inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm"
            >
              {saving ? <span className="spin" /> : <><Save className="w-4 h-4" /> Save sheet</>}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
